"use client";

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useComponentTranslations } from "@/app/components/LanguageProvider";
import { useLocale } from "@/app/components/LocaleProvider";
import { getPathWithLocale } from "@/lib/i18n";
import { useIsVisible } from '@/hooks/useIsVisible';
import { useNearViewport } from '@/hooks/useNearViewport';
import Link from "next/link";
import Reveal from "@/app/components/Reveal";

const industries = [
    { id: 'finance', titleKey: 'industryFinanceTitle', descKey: 'industryFinanceDesc', image: '/images/finance.webp', fallback: '/images/finance.jpg' },
    { id: 'telecom', titleKey: 'industryTelecomTitle', descKey: 'industryTelecomDesc', image: '/images/telecom.webp', fallback: '/images/telecom.jpg' },
    { id: 'hospitality', titleKey: 'industryHospitalityTitle', descKey: 'industryHospitalityDesc', image: '/images/hospitality.webp', fallback: '/images/hospitality.jpg' },
    { id: 'logistics', titleKey: 'industryLogisticsTitle', descKey: 'industryLogisticsDesc', image: '/images/logistics.webp', fallback: '/images/logistics.jpg' },
    { id: 'retail', titleKey: 'industryRetailTitle', descKey: 'industryRetailDesc', image: '/images/retail.webp', fallback: '/images/retail.jpg' },
    { id: 'healthcare', titleKey: 'industryHealthcareTitle', descKey: 'industryHealthcareDesc', image: '/images/healthcare.webp', fallback: '/images/healthcare.jpeg' },
    { id: 'education', titleKey: 'industryEducationTitle', descKey: 'industryEducationDesc', image: '/images/education.webp', fallback: '/images/education.jpg' },
    { id: 'marketing', titleKey: 'industryMarketingTitle', descKey: 'industryMarketingDesc', image: '/images/marketing.webp', fallback: '/images/marketing.jpg' },
    { id: 'sports', titleKey: 'industrySportsTitle', descKey: 'industrySportsDesc', image: '/images/sports.webp', fallback: '/images/sports.jpg' },
    { id: 'agriculture', titleKey: 'industryAgricultureTitle', descKey: 'industryAgricultureDesc', image: '/images/agriculture.webp', fallback: '/images/agriculture.jpg' },
    { id: 'utilities', titleKey: 'industryUtilitiesTitle', descKey: 'industryUtilitiesDesc', image: '/images/utilities.webp', fallback: '/images/utilities.jpg' },
    { id: 'insurance', titleKey: 'industryInsuranceTitle', descKey: 'industryInsuranceDesc', image: '/images/insurance.webp', fallback: '/images/insurance.jpg' },
];

const IndustriesSection = () => {
    const { t } = useComponentTranslations("IndustriesSection");
    const locale = useLocale();
    const swiperRef = useRef<HTMLDivElement>(null);
    const swiperInstanceRef = useRef<{ autoplay: { start: () => void; stop: () => void }; destroy: () => void } | null>(null);
    const { ref: nearRef, near } = useNearViewport('600px');
    const { ref: sectionRef, isVisible } = useIsVisible(0.5);

    useEffect(() => {
        if (!near || !swiperRef.current) return;
        let cancelled = false;

        Promise.all([
            import('@/app/components/swiper-lite'),
            import('swiper/css').catch(() => null),
        ]).then(([mod]) => {
            if (cancelled || !swiperRef.current) return;
            if ((swiperRef.current as { swiper?: unknown }).swiper) return;

            const swiper = mod.createAutoplaySwiper(swiperRef.current, {
                slidesPerView: 1,
                spaceBetween: 16,
                loop: true,
                autoplay: {
                    delay: 3000,
                    disableOnInteraction: false,
                },
                breakpoints: {
                    640: { slidesPerView: 2, spaceBetween: 20 },
                    768: { slidesPerView: 3, spaceBetween: 24 },
                    1024: { slidesPerView: 4, spaceBetween: 32 },
                },
            });
            swiperInstanceRef.current = swiper;
            swiper.autoplay.stop();
        });

        return () => {
            cancelled = true;
            swiperInstanceRef.current?.destroy();
            swiperInstanceRef.current = null;
        };
    }, [near]);

    useEffect(() => {
        const swiper = swiperInstanceRef.current;
        if (!swiper) return;
        if (isVisible) {
            swiper.autoplay.start();
        } else {
            swiper.autoplay.stop();
        }
    }, [isVisible]);

    return (
        <div ref={sectionRef} className="py-16 sm:py-24 max-md:py-9">
            <div ref={nearRef} className="w-full max-w-7xl mx-auto px-4">
                <div className="text-center max-md:mb-8 mb-12">
                    <Reveal y={50} duration={0.8}>
                        <h2 className="text-3xl font-bold tracking-tight text-white/95">
                            {t("sectionIndustries")}
                        </h2>
                    </Reveal>
                    <Reveal y={50} duration={0.8}>
                        <p className="mt-3 text-sm text-white/70 max-w-2xl mx-auto">
                            {t("industriesIntro")}
                        </p>
                    </Reveal>
                </div>

                <div className="relative">
                    <div 
                        ref={swiperRef}
                        className="swiper"
                    >
                        <div className="swiper-wrapper">
                            {industries.map((industry) => (
                                <div className="swiper-slide" key={industry.id}>
                                    <Link href={getPathWithLocale(`/industries/${industry.id}`, locale)} className="block max-md:h-64 h-96">
                                        <div
                                            className="relative h-full overflow-hidden border border-white/30"
                                        >
                                            <Image 
                                                src={industry.image} 
                                                alt={industry.id}
                                                fill
                                                sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                                                className="object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    if (target.src.includes('.webp') && industry.fallback) {
                                                        target.src = industry.fallback;
                                                    }
                                                }}
                                            />
                                            <div className="absolute bottom-0 left-0 flex max-md:h-28 h-37.5 w-full flex-col justify-center glass-overlay max-md:px-3 max-md:py-1 px-5 py-2.5 text-white">
                                                <h3 className="text-xl font-semibold text-white max-md:mb-2 mb-4">{t(industry.titleKey)}</h3>
                                                <p className="text-sm text-white/70 leading-normal">{t(industry.descKey)}</p>
                                            </div>
                                        </div>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndustriesSection;
